import React from "react";
import uniqBy from "lodash/uniqBy";

import { Action, Category } from "@/constants/events";
import { Deal } from "@/custom-types/Deals";
import useDomainCountryCode from "@/hooks/useDomainCountryCode";
import { useEventTracker } from "@/hooks/useEventTracker";
import { useDispatch, useSelector } from "@/redux/hooks";
import { getUserId } from "@/redux/selectors/user";
import { dealCardUpdateMedRec } from "@/utils/dealCardUpdateMedRec";
import { getDispensaryUrl } from "@/utils/dispensaryUrls";
import { getRoundedDistance } from "@/utils/distance";

import Carousel from "@/components/botanic/Carousel";
import DealCard from "@/components/botanic/DealCard";

const BundleCarousel: React.FC<{ bundleDeals: Deal[] }> = ({ bundleDeals }) => {
  const domainCountryCode = useDomainCountryCode();
  const userId = useSelector(getUserId);
  const dispatch = useDispatch();
  const { publishEvent } = useEventTracker();

  if (!bundleDeals.length) {
    return null;
  }

  const uniqueDispensaryDeals = uniqBy(bundleDeals, "dispensary.name");
  // Use unique dispensary deals if there are at least 3 to show
  const displayedDeals =
    uniqueDispensaryDeals.length >= 3
      ? uniqueDispensaryDeals
      : bundleDeals.slice(0, 8);

  const cardClick = async (deal: Deal) => {
    const {
      dispensary: { id: dispensaryId, distanceMi },
      id: dealId,
      title,
      isMedical,
      isRecreational,
    } = deal;

    publishEvent({
      action: Action.click,
      category: Category.dealsBundleCard,
      dealId,
      dispensaryDistance: getRoundedDistance(distanceMi, domainCountryCode),
      dispensaryId,
      label: title,
      userId: userId as number,
    });

    // Med/rec deals will have medical and/or recreational properties, check if we need to update the user preference
    if (isMedical || isRecreational) {
      await dealCardUpdateMedRec(deal, dispatch);
    }
  };

  return (
    <Carousel
      className="mb-xxl"
      title={<h2 className="heading--m">Bundled weed deals</h2>}
      subtitle="Quantity discounts"
    >
      {displayedDeals.map((deal) => {
        const {
          title,
          dispensary: { slug },
        } = deal;
        const isDealCBD = !deal.isMedical && !deal.isRecreational;
        const dealType = deal.isMedical && !deal.isRecreational ? "Med" : "Rec";

        const dealUrl = getDispensaryUrl({
          countryCode: domainCountryCode,
          query: {
            deal_title: [title],
            ...(!isDealCBD && { menu_type: dealType }),
          },
          slug,
          tab: "menu",
        });

        return (
          <DealCard
            className="rounded h-full overflow-hidden shadow-low"
            countryCode={domainCountryCode}
            deal={{ ...deal, dealUrl }}
            disableLazyLoad={!!bundleDeals.length}
            key={deal.id}
            onClick={() => cardClick(deal)}
            showFulfillment={true}
          />
        );
      })}
    </Carousel>
  );
};

export default BundleCarousel;
